import React, { useEffect, useMemo, useRef, useState } from "react";
import { applyLicenseKey } from "../../../../../../utils/License";
import {
  addStage,
  deleteStage,
  getStage,
  updateStage,
} from "./LeadStageListService";
import ConfirmationModel from "../../../../../../global/components/ConfirmationModal/confirmationModel";
import YhInput from "../../../../../../ui/YhInput";
import YhSelect from "../../../../../../ui/YhSelect";
import YhButton from "../../../../../../ui/YhButton";
import YhTable, {
  YhTableColumn,
  YhTableCellParams,
} from "../../../../../../ui/YhTable";

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const STAGE_TYPE_OPTIONS = [
  { label: "Sale", value: "1" },
  { label: "PreSale", value: "0" },
  { label: "Both", value: "-1" },
];

const VISIBILITY_OPTIONS = [
  { label: "All users with access", value: "all" },
  {
    label: "Owner + Team Lead + Super Admin only",
    value: "owner_tl_admin",
  },
];

const stageTypeLabel = (value: number) => {
  if (value === 1) return "Sale";
  if (value === -1) return "Both";
  return "PreSale";
};

const visibilityLabel = (value: string) => {
  if (value === "owner_tl_admin" || value === "restricted") {
    return "Owner + TL + Admin";
  }
  return "All users";
};

type FlatStage = {
  id: string;
  isSale: number;
  stage_Name: string;
  parentStageId: string | null;
  path: string[];
  visibility: string;
  convertToCustomer: boolean;
  tabIndex: number | null;
};

const flattenData = (data: any[], parentPath: string[] = []): FlatStage[] => {
  const flattened: FlatStage[] = [];

  (data || []).forEach((item) => {
    const currentPath = [...parentPath, item.stage_Name];
    flattened.push({
      id: item._id,
      isSale: item.isSale,
      stage_Name: item.stage_Name,
      parentStageId: item.parentStageId || null,
      path: currentPath,
      visibility:
        item.visibility === "owner_tl_admin" || item.visibility === "restricted"
          ? "owner_tl_admin"
          : "all",
      convertToCustomer: Boolean(item.convertToCustomer),
      tabIndex:
        item.tabIndex == null || item.tabIndex === ""
          ? null
          : Number.isFinite(Number(item.tabIndex))
            ? Number(item.tabIndex)
            : null,
    });

    if (item.sub_Stage_name?.length) {
      flattened.push(...flattenData(item.sub_Stage_name, currentPath));
    }
  });

  return flattened;
};

const LeadStageList = () => {
  const [stageDrawer, setStageDrawer] = useState(false);
  const [error, setError] = useState("");
  const [stageState, setStageState] = useState<any>({});
  const [showAdditionalField, setShowAdditionalField] = useState(false);
  const [openSubStagePop, setOpenSubStagePop] = useState(false);
  const [allStageData, setAllStageData] = useState<any[]>([]);
  const [selectedStage, setSelectedStage] = useState<any>(null);
  const [callStageData, setCallStageData] = useState<FlatStage[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<null | any>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    applyLicenseKey();
    handleTableData();
  }, []);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const closeDrawer = () => {
    setStageDrawer(false);
    setShowAdditionalField(false);
    setStageState({});
    setSelectedStage(null);
    setError("");
  };

  const nextTabIndex = () => {
    const used = callStageData
      .filter((row) => !row.parentStageId)
      .map((row) => Number(row.tabIndex))
      .filter((n) => Number.isFinite(n));
    return used.length ? Math.max(...used) + 1 : 1;
  };

  const openCreate = () => {
    setStageState({
      isSale: 0,
      visibility: "all",
      convertToCustomer: false,
      tabIndex: nextTabIndex(),
    });
    setShowAdditionalField(false);
    setSelectedStage(null);
    setError("");
    setStageDrawer(true);
  };

  const handleTableData = async () => {
    try {
      setLoading(true);
      const response = await getStage();
      const flattenedData = flattenData(response.data || []);
      setCallStageData(flattenedData);

      setExpanded((prev) => {
        const next: Record<string, boolean> = {};
        flattenedData.forEach((row) => {
          const hasKids = flattenedData.some((r) => r.parentStageId === row.id);
          if (hasKids) next[row.id] = prev[row.id] ?? true;
        });
        return next;
      });
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const getStageData = async () => {
    try {
      const data = await getStage();
      setAllStageData(data?.data || []);
    } catch {
      setAllStageData([]);
    }
  };

  const visibleRows = useMemo(() => {
    const filtered = callStageData.filter((row) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        row.stage_Name?.toLowerCase().includes(q) ||
        stageTypeLabel(row.isSale).toLowerCase().includes(q) ||
        visibilityLabel(row.visibility).toLowerCase().includes(q)
      );
    });

    if (search.trim()) return filtered;

    return filtered.filter((row) => {
      if (!row.parentStageId) return true;
      let parentId: string | null = row.parentStageId;
      while (parentId) {
        if (!expanded[parentId]) return false;
        const parent = callStageData.find((t) => t.id === parentId);
        parentId = parent?.parentStageId || null;
      }
      return true;
    });
  }, [callStageData, expanded, search]);

  const handleSubmit = async () => {
    if (!stageState?.stage_Name) {
      setError("This field is required");
      return;
    }

    try {
      setSubmitting(true);
      const isEditingSubStage = selectedStage?.parentStageId != null;
      const payload = {
        stage_Name: stageState?.stage_Name || "",
        parentStageId: showAdditionalField
          ? isEditingSubStage
            ? selectedStage?.parentStageId
            : selectedStage?._id || selectedStage?.id || null
          : null,
        isSale: stageState.isSale ?? 0,
        visibility:
          stageState.visibility === "owner_tl_admin" ||
          stageState.visibility === "restricted"
            ? "owner_tl_admin"
            : "all",
        convertToCustomer: Boolean(stageState.convertToCustomer),
        tabIndex:
          stageState.tabIndex === "" || stageState.tabIndex == null
            ? null
            : Number(stageState.tabIndex),
        id: stageState.id || null,
      };

      const response = !payload.id
        ? await addStage(payload)
        : await updateStage(payload);

      if (response?.success === 0) {
        setError(response?.message || "Could not save stage");
        return;
      }
      if (response) {
        await handleTableData();
        closeDrawer();
      }
    } catch (err: any) {
      setError(err?.message || "Could not save stage");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteStageData = async (id: string) => {
    try {
      const response = await deleteStage(id);
      if (response.success === 1) {
        await handleTableData();
        setDialogOpen(false);
        setSelectedRow(null);
      }
    } catch {
      /* ignore */
    }
  };

  const handleSelectStage = (stage: any) => {
    setSelectedStage(stage);
    setStageState({
      ...stageState,
      additionalDescription: stage.stage_Name,
    });
    setOpenSubStagePop(false);
  };

  const renderStageTree = (stages: any[], level = 0): React.ReactNode =>
    (stages || []).map((stage) => (
      <div key={stage._id}>
        <button
          type="button"
          className="mb-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-[#F8FAFC]"
          style={{ paddingLeft: 12 + level * 16 }}
          onClick={() => handleSelectStage(stage)}
        >
          <span className="font-medium text-[#111827]">{stage.stage_Name}</span>
          <span
            className={`h-4 w-4 rounded-full border ${
              (selectedStage?._id || selectedStage?.id) === stage._id
                ? "border-[#A02448] bg-[#A02448]"
                : "border-[#D1D5DB]"
            }`}
          />
        </button>
        {stage.sub_Stage_name?.length
          ? renderStageTree(stage.sub_Stage_name, level + 1)
          : null}
      </div>
    ));

  const columns: YhTableColumn[] = [
    {
      headerName: "Stage Name",
      field: "stage_Name",
      minWidth: 320,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const row = params.row as FlatStage;
        const depth = Math.max(0, (row.path?.length || 1) - 1);
        const hasChildren = callStageData.some(
          (t) => t.parentStageId === row.id
        );
        const isOpen = !!expanded[row.id];

        return (
          <div
            className="flex items-center gap-2"
            style={{ paddingLeft: depth * 20 }}
          >
            <button
              type="button"
              className={`flex h-6 w-6 items-center justify-center rounded text-[#6B7280] ${
                hasChildren ? "hover:bg-[#F3F4F6]" : "invisible"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (hasChildren) {
                  setExpanded((prev) => ({
                    ...prev,
                    [row.id]: !prev[row.id],
                  }));
                }
              }}
            >
              <Chevron open={isOpen} />
            </button>
            <span className="text-sm font-semibold capitalize text-[#111827]">
              {row.stage_Name || "—"}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "Tab index",
      field: "tabIndex",
      minWidth: 110,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm font-medium text-[#111827]">
          {params.row?.tabIndex == null || params.row?.tabIndex === ""
            ? "—"
            : params.row.tabIndex}
        </span>
      ),
    },
    {
      headerName: "Stage Type",
      field: "isSale",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="inline-flex rounded-full bg-[#F3F4F6] px-2.5 py-0.5 text-xs font-medium text-[#374151]">
          {stageTypeLabel(params.row?.isSale)}
        </span>
      ),
    },
    {
      headerName: "Visibility",
      field: "visibility",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => {
        const restricted =
          params.row?.visibility === "owner_tl_admin" ||
          params.row?.visibility === "restricted";
        return (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
              restricted
                ? "bg-[#FEF3C7] text-[#92400E]"
                : "bg-[#F3F4F6] text-[#374151]"
            }`}
          >
            {visibilityLabel(params.row?.visibility)}
          </span>
        );
      },
    },
    {
      headerName: "Convert to Customer",
      field: "convertToCustomer",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
            params.row?.convertToCustomer
              ? "bg-[#DCFCE7] text-[#166534]"
              : "bg-[#F3F4F6] text-[#6B7280]"
          }`}
        >
          {params.row?.convertToCustomer ? "Yes" : "No"}
        </span>
      ),
    },
    {
      headerName: "Action",
      field: "action",
      minWidth: 90,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          data-action-button="true"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
          onClick={(e) => {
            e.stopPropagation();
            const rect = (
              e.currentTarget as HTMLButtonElement
            ).getBoundingClientRect();
            setMenuPos({ top: rect.bottom + 4, left: rect.left - 160 });
            setSelectedRow(params.row);
            setMenuOpen(true);
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>
      ),
    },
  ];

  const isEdit = !!stageState.id;

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1E1B4B] sm:text-3xl">
            Meta Stage
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Set tab index to control My Lead tab order. Lower numbers appear first after All.
          </p>
        </div>
        <YhButton variant="primary" size="sm" onClick={openCreate}>
          + Create Stage
        </YhButton>
      </div>

      <div className="mb-3 shrink-0 sm:max-w-[420px]">
        <YhInput
          placeholder="Search stages…"
          value={search}
          type="search"
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <YhTable
          columns={columns}
          rows={visibleRows}
          getRowId={(row) => row.id}
          loading={loading}
          emptyMessage="No stages found"
          className="h-full"
          showPagination={false}
        />
      </div>

      {menuOpen && menuPos ? (
        <div
          ref={menuRef}
          className="fixed z-[1500] w-48 overflow-hidden rounded-xl border border-[#EEF0F5] bg-white py-1 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <p className="px-3 py-2 text-xs font-semibold uppercase text-[#9CA3AF]">
            Actions
          </p>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F8FAFC]"
            onClick={() => {
              const row = selectedRow || {};
              setStageState({
                ...row,
                visibility:
                  row.visibility === "owner_tl_admin" ||
                  row.visibility === "restricted"
                    ? "owner_tl_admin"
                    : "all",
                convertToCustomer: Boolean(row.convertToCustomer),
              });
              setShowAdditionalField(!!row?.parentStageId);
              if (row?.parentStageId) {
                setSelectedStage(row);
                setStageState({
                  ...row,
                  additionalDescription: "",
                  visibility:
                    row.visibility === "owner_tl_admin" ||
                    row.visibility === "restricted"
                      ? "owner_tl_admin"
                      : "all",
                  convertToCustomer: Boolean(row.convertToCustomer),
                });
              }
              setStageDrawer(true);
              setMenuOpen(false);
            }}
          >
            Edit
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#DC2626] hover:bg-[#FEF2F2]"
            onClick={() => {
              setDialogOpen(true);
              setMenuOpen(false);
            }}
          >
            Delete
          </button>
        </div>
      ) : null}

      {stageDrawer ? (
        <div
          className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4"
          onClick={closeDrawer}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[#EEF0F5] px-5 py-4">
              <h2 className="text-lg font-bold text-[#111827]">
                {isEdit ? "Edit Stage" : "Create Stage"}
              </h2>
              <button
                type="button"
                onClick={closeDrawer}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <YhSelect
                  label="Stage Type"
                  placeholder="Select"
                  value={String(stageState?.isSale ?? 0)}
                  options={STAGE_TYPE_OPTIONS}
                  onChange={(e) =>
                    setStageState({
                      ...stageState,
                      isSale: Number(e.target.value),
                    })
                  }
                />

                <YhInput
                  label="Stage Name"
                  requiredMark
                  placeholder="Stage name"
                  value={stageState?.stage_Name || ""}
                  error={error || undefined}
                  onChange={(e) => {
                    const name = e.target.value;
                    const next: any = {
                      ...stageState,
                      stage_Name: name,
                    };
                    // Sensible defaults when naming known stages
                    if (
                      /^(pipeline|junk|visit\s*planned)$/i.test(name.trim()) &&
                      !stageState.id
                    ) {
                      next.visibility = "owner_tl_admin";
                    }
                    if (/^customer$/i.test(name.trim()) && !stageState.id) {
                      next.convertToCustomer = true;
                    }
                    setStageState(next);
                    if (name) setError("");
                  }}
                />

                <YhInput
                  label="Tab index"
                  placeholder="1"
                  type="number"
                  value={
                    stageState?.tabIndex == null || stageState?.tabIndex === ""
                      ? ""
                      : String(stageState.tabIndex)
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    setStageState({
                      ...stageState,
                      tabIndex: raw === "" ? "" : Number(raw),
                    });
                  }}
                />
                <p className="-mt-2 text-xs text-[#6B7280] sm:col-span-2">
                  Lower numbers appear first on My Lead after All. Example: Lead
                  1, Prospect 2, Closure 3.
                </p>

                <YhSelect
                  label="Lead visibility in this stage"
                  placeholder="Select"
                  value={String(stageState?.visibility || "all")}
                  options={VISIBILITY_OPTIONS}
                  onChange={(e) =>
                    setStageState({
                      ...stageState,
                      visibility: e.target.value,
                    })
                  }
                />

                <div className="sm:col-span-2">
                  <label className="mt-1 flex cursor-pointer items-start gap-2 text-sm text-[#374151]">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-[#D1D5DB]"
                      checked={Boolean(stageState?.convertToCustomer)}
                      onChange={(e) =>
                        setStageState({
                          ...stageState,
                          convertToCustomer: e.target.checked,
                        })
                      }
                    />
                    <span>
                      <span className="font-medium text-[#111827]">
                        Convert to customer on this stage
                      </span>
                      <span className="mt-0.5 block text-xs text-[#6B7280]">
                        When a lead moves here, create/update the customer
                        record automatically.
                      </span>
                    </span>
                  </label>
                  {(stageState?.visibility === "owner_tl_admin" ||
                    stageState?.visibility === "restricted") && (
                    <p className="mt-2 rounded-lg bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
                      Restricted: only the user who moved the lead here, their
                      team lead, and super admin can see or act on it. Other RMs
                      are removed from the lead. Admin All Leads still shows it.
                      Sub-stages under this category inherit the same rule.
                    </p>
                  )}
                </div>

                {showAdditionalField ? (
                  <div className="sm:col-span-2">
                    <label className="yh-label">Parent Category</label>
                    <button
                      type="button"
                      className="yh-input w-full text-left"
                      onClick={() => {
                        setOpenSubStagePop(true);
                        getStageData();
                      }}
                    >
                      {stageState?.additionalDescription ||
                        selectedStage?.stage_Name ||
                        "Select"}
                    </button>
                  </div>
                ) : null}
              </div>

              <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-[#374151]">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-[#D1D5DB]"
                  checked={showAdditionalField}
                  onChange={(e) => {
                    setShowAdditionalField(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedStage(null);
                      setStageState({
                        ...stageState,
                        additionalDescription: "",
                      });
                    }
                  }}
                />
                Add as Sub Category
              </label>
            </div>

            <div className="flex shrink-0 justify-end gap-3 border-t border-[#EEF0F5] bg-[#FAFBFC] px-5 py-4">
              <YhButton variant="secondary" size="sm" onClick={closeDrawer}>
                Cancel
              </YhButton>
              <YhButton
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Saving…" : "Submit"}
              </YhButton>
            </div>
          </div>
        </div>
      ) : null}

      {openSubStagePop ? (
        <div
          className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setOpenSubStagePop(false)}
        >
          <div
            className="flex max-h-[70vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#EEF0F5] px-5 py-4">
              <h3 className="text-base font-bold text-[#111827]">
                Select Parent Stage
              </h3>
              <button
                type="button"
                onClick={() => setOpenSubStagePop(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto px-3 py-3">
              {allStageData.length
                ? renderStageTree(allStageData)
                : (
                  <p className="px-2 py-4 text-sm text-[#9CA3AF]">
                    No stages available
                  </p>
                )}
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmationModel
        open={dialogOpen}
        title="Confirmation"
        description="Are you sure you want to delete?"
        onConfirm={() => selectedRow?.id && deleteStageData(selectedRow.id)}
        onCancel={() => setDialogOpen(false)}
      />
    </div>
  );
};

export default LeadStageList;
